/**
 * School Growth table: class enrollments (with the students behind them),
 * money collected (with the partner-fee share named underneath) and
 * discounts given, per period in the school's calendar.
 *
 * Works on touch and with a screen reader: the student count and partner
 * share are visible text, and each column's definition sits behind an info
 * button whose tooltip opens on tap (describeChild, enterTouchDelay 0). At
 * phone width each period's name sits on its own line above its three
 * figures, the icons drop out and the headers shorten, so nothing is clipped
 * or needs sideways scrolling.
 */

import React from "react";
import {
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CalendarToday as ThisMonthIcon,
  EventNote as LastMonthIcon,
  DateRange as ThisYearIcon,
  History as LastYearIcon,
  AllInclusive as AllTimeIcon,
  PersonAdd as EnrollIcon,
  AttachMoney as MoneyIcon,
  Discount as DiscountIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import type { Theme } from "@mui/material/styles";
import type { GrowthPeriodStats, GrowthStats } from "@/types/admin";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const growthPeriodConfig = [
  { label: "This Month", key: "current_month", icon: <ThisMonthIcon />, color: "#5C6BC0" },
  { label: "Last Month", key: "last_month", icon: <LastMonthIcon />, color: "#7E57C2" },
  { label: "This Year", key: "current_year", icon: <ThisYearIcon />, color: "#26A69A" },
  { label: "Last Year", key: "last_year", icon: <LastYearIcon />, color: "#78909C" },
  { label: "All Time", key: "all_time", icon: <AllTimeIcon />, color: "#AB47BC" },
] as const;

/** What each column counts. Keep in step with the dashboard help guide. */
export const GROWTH_COLUMN_HELP = {
  enrollments:
    "Class enrollments created in the period, whatever their status now: active, paused, completed or withdrawn. A student in two classes counts twice (the number under it is how many students that is); a transfer counts once.",
  collected:
    "Family payments received in the period, minus refunds and chargebacks, dated by payment date. Sponsor payments (such as TEFA) are not included. It uses the same rule as Net on the Collections report, but that report counts UTC days while this follows your school's time zone, so totals can differ around midnight at month ends. It includes partner fees collected on behalf of a partner institution, shown underneath.",
  discounts:
    "Discounts on invoices issued in the period, dated by the invoice's issue date (a discount added today to a September invoice counts in September): discount lines, sibling discounts, mid-month proration, student discounts such as Zakat, and discounts added with Apply Discount. Billing corrections, withdrawal credits and late-fee removals are not discounts, unless the credit was given as a discount (linked to a discount rule).",
} as const;

export const PARTNER_HELP =
  "Fees collected on behalf of a partner institution, which the school passes on. Counted the same way as Partner on the Billed vs Collected report.";

const MINUS = "−";

/** "$1,234.50", or "−$50.00" for a negative amount (API decimals are strings). */
export const formatMoney = (value: number | string | undefined | null): string => {
  const n = Number(value ?? 0);
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // A tiny negative that rounds to "$0.00" gets no minus sign.
  return n < 0 && abs !== "0.00" ? `${MINUS}$${abs}` : `$${abs}`;
};

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

/** "13 students", or null when an older API sends no student count. */
export const studentCaption = (data: GrowthPeriodStats): string | null => {
  if (data.distinct_students === undefined || data.distinct_students === null) return null;
  return plural(Number(data.distinct_students), "student", "students");
};

/** "14 class enrollments by 13 students" (no student count from an older API). */
export const enrollmentSummary = (data: GrowthPeriodStats): string => {
  const enrollments = plural(Number(data.new_enrollments), "class enrollment", "class enrollments");
  const students = studentCaption(data);
  return students ? `${enrollments} by ${students}` : enrollments;
};

/**
 * "incl. $6,250.00 in partner fees"; a period with more partner refunds than
 * partner payments reads "incl. −$50.00 in partner fees (net of refunds)".
 * Null when there is none, or when the API could not work it out (null).
 */
export const partnerCaption = (data: GrowthPeriodStats): string | null => {
  if (data.partner_collected === undefined || data.partner_collected === null) return null;
  const partner = Number(data.partner_collected);
  if (!partner) return null;
  const amount = formatMoney(partner);
  return partner < 0 ? `incl. ${amount} in partner fees (net of refunds)` : `incl. ${amount} in partner fees`;
};

// Money colours that keep AA contrast in both themes (the .dark shades on a
// light background, the .light shades on a dark one).
const toneColor = (tone: "success" | "warning") => (theme: Theme) =>
  theme.palette.mode === "dark" ? theme.palette[tone].light : theme.palette[tone].dark;

const captionSx = { display: "block", color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.25 } as const;

const moneySx = (tone: "success" | "warning") => ({
  display: "block",
  color: toneColor(tone),
  fontWeight: 500,
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
});

const rowAnimation = (idx: number) => ({
  animation: `${fadeSlideIn} 0.4s ease-out ${idx * 0.1}s both`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

const HeaderCell: React.FC<{
  icon: React.ReactElement<{ sx?: object }>;
  label: string;
  shortLabel: string;
  help: string;
  compact: boolean;
}> = ({ icon, label, shortLabel, help, compact }) => (
  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.8rem" }}>
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, justifyContent: "flex-end" }}>
      {!compact && React.cloneElement(icon, { sx: { fontSize: 13, mr: 0.25 } })}
      {compact ? shortLabel : label}
      <Tooltip title={help} describeChild enterTouchDelay={0} leaveTouchDelay={6000} arrow>
        <IconButton size="small" aria-label={`What ${label} counts`} sx={{ p: 0.25, color: "text.secondary" }}>
          <InfoIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </TableCell>
);

interface SchoolGrowthTableProps {
  growth: GrowthStats;
  /** Phone layout (each period's name on its own line above its figures).
   * Defaults to the viewport being narrower than the theme's sm breakpoint. */
  compact?: boolean;
}

export const SchoolGrowthTable: React.FC<SchoolGrowthTableProps> = ({ growth, compact: compactProp }) => {
  const theme = useTheme();
  // noSsr: decide on the first render, so a phone never mounts the desktop
  // rows first and then swaps them (replaying the slide-in).
  const narrow = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const compact = compactProp ?? narrow;
  const inBody = (rows: React.ReactNode) => (compact ? rows : <TableBody>{rows}</TableBody>);
  return (
    <Card sx={{ boxShadow: 1, overflow: "hidden" }}>
      <TableContainer>
        <Table size="small" sx={{ "& td, & th": { px: compact ? 0.5 : 1, py: 0.5 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              {!compact && (
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.8rem", width: 130 }}>
                  Period
                </TableCell>
              )}
              <HeaderCell
                icon={<EnrollIcon />}
                label="Class enrollments"
                shortLabel="Enrolled"
                help={GROWTH_COLUMN_HELP.enrollments}
                compact={compact}
              />
              <HeaderCell
                icon={<MoneyIcon />}
                label="Collected"
                shortLabel="Collected"
                help={GROWTH_COLUMN_HELP.collected}
                compact={compact}
              />
              <HeaderCell
                icon={<DiscountIcon />}
                label="Discounts"
                shortLabel="Discounts"
                help={GROWTH_COLUMN_HELP.discounts}
                compact={compact}
              />
            </TableRow>
          </TableHead>
          {/* Phone layout: one <tbody> per period, so its name (scope="rowgroup")
              heads only its own figures for screen readers. */}
          {inBody(
            growthPeriodConfig.map((period, idx) => {
              const data = growth[period.key];
              // An API deployed before last_month existed omits it.
              if (!data) return null;
              const isAllTime = period.key === "all_time";
              const students = studentCaption(data);
              const partner = partnerCaption(data);
              const shade = isAllTime ? { bgcolor: "action.hover" } : {};
              const label = (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {!compact && (
                    <Box
                      aria-hidden
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        borderRadius: "6px",
                        bgcolor: `${period.color}14`,
                        color: period.color,
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(period.icon, { sx: { fontSize: 16 } })}
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: isAllTime || compact ? 700 : 500, fontSize: "0.8rem", whiteSpace: "nowrap" }}
                  >
                    {period.label}
                  </Typography>
                </Box>
              );
              const figures = (
                <>
                  <TableCell align="right">
                    <Typography variant="caption" component="span" sx={{ display: "block", fontSize: "0.8rem" }}>
                      {Number(data.new_enrollments).toLocaleString("en-US")}
                    </Typography>
                    {students && (
                      <Typography variant="caption" component="span" sx={captionSx}>
                        {students}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" component="span" sx={moneySx("success")}>
                      {formatMoney(data.tuition_collected)}
                    </Typography>
                    {partner && (
                      <Tooltip title={PARTNER_HELP} describeChild enterTouchDelay={0} leaveTouchDelay={6000} arrow>
                        <Typography variant="caption" component="span" tabIndex={0} sx={{ ...captionSx, cursor: "help" }}>
                          {partner}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" component="span" sx={moneySx("warning")}>
                      {formatMoney(data.discounts_given)}
                    </Typography>
                  </TableCell>
                </>
              );
              if (compact) {
                return (
                  <TableBody key={period.key}>
                    <TableRow sx={{ ...rowAnimation(idx), ...shade }}>
                      <TableCell component="th" scope="rowgroup" colSpan={3} sx={{ borderBottom: 0, pb: 0 }}>
                        {label}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      data-testid={`growth-row-${period.key}`}
                      sx={{ ...rowAnimation(idx), ...shade, ...(isAllTime && { "& td": { border: 0 } }) }}
                    >
                      {figures}
                    </TableRow>
                  </TableBody>
                );
              }
              return (
                <TableRow
                  key={period.key}
                  data-testid={`growth-row-${period.key}`}
                  sx={{
                    "&:last-child td": { border: 0 },
                    "&:hover": { bgcolor: "action.selected" },
                    ...rowAnimation(idx),
                    ...shade,
                  }}
                >
                  <TableCell component="th" scope="row">
                    {label}
                  </TableCell>
                  {figures}
                </TableRow>
              );
            }),
          )}
        </Table>
      </TableContainer>
    </Card>
  );
};
